import { useMemo } from "react";
import { DynIcon } from "@/lib/icons";

// Corridor waypoints over the India map image (viewBox 0..100 percent coords),
// forming a plausible national logistics route north -> south.
const CORRIDOR = [
  [53, 24],
  [47, 40],
  [37, 55],
  [50, 69],
  [63, 84],
];

function buildGeometry(count) {
  // segment lengths of the corridor polyline
  const segs = [];
  let total = 0;
  for (let i = 1; i < CORRIDOR.length; i++) {
    const dx = CORRIDOR[i][0] - CORRIDOR[i - 1][0];
    const dy = CORRIDOR[i][1] - CORRIDOR[i - 1][1];
    const len = Math.hypot(dx, dy);
    segs.push(len);
    total += len;
  }
  const at = (t) => {
    let d = t * total;
    for (let i = 0; i < segs.length; i++) {
      if (d <= segs[i] || i === segs.length - 1) {
        const r = segs[i] === 0 ? 0 : d / segs[i];
        return [
          CORRIDOR[i][0] + (CORRIDOR[i + 1][0] - CORRIDOR[i][0]) * r,
          CORRIDOR[i][1] + (CORRIDOR[i + 1][1] - CORRIDOR[i][1]) * r,
        ];
      }
      d -= segs[i];
    }
    return CORRIDOR[CORRIDOR.length - 1];
  };
  const nodes = [];
  for (let i = 0; i < count; i++) nodes.push(at(count > 1 ? i / (count - 1) : 0));
  const path = "M " + CORRIDOR.map((p) => `${p[0]} ${p[1]}`).join(" L ");
  return { nodes, path };
}

export function IndiaJourneyMap({ stages, active, onSelect, imageUrl }) {
  const count = stages.length;
  const { nodes, path } = useMemo(() => buildGeometry(count), [count]);
  const fraction = count > 1 ? active / (count - 1) : 1;

  return (
    <div className="relative h-[38vh] landscape:h-[56vh] w-full overflow-hidden rounded-3xl glow-border bg-[#050b18]" data-testid="india-journey-map">
      {imageUrl && <img src={imageUrl} alt="India network map" className="absolute inset-0 h-full w-full object-cover opacity-80" />}
      <div className="absolute inset-0 bg-gradient-to-t from-[#050b18]/70 via-transparent to-[#050b18]/30" />

      <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
        {/* faint full route */}
        <path d={path} fill="none" stroke="rgba(0,229,255,0.18)" strokeWidth="0.7" strokeLinecap="round" strokeLinejoin="round" />
        {/* glowing active route (draws up to active stage) */}
        <path
          d={path}
          fill="none"
          stroke="#00e5ff"
          strokeWidth="1"
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength="1"
          strokeDasharray="1"
          strokeDashoffset={1 - fraction}
          style={{ transition: "stroke-dashoffset 0.9s ease", filter: "drop-shadow(0 0 1.4px #00e5ff)" }}
        />
      </svg>

      {/* stage nodes */}
      {nodes.map((p, i) => {
        const reached = i <= active;
        const isActive = i === active;
        return (
          <button
            key={i}
            data-testid={`india-map-node-${i}`}
            onClick={() => onSelect(i)}
            className="absolute -translate-x-1/2 -translate-y-1/2 no-select"
            style={{ left: `${p[0]}%`, top: `${p[1]}%` }}
            aria-label={stages[i].title}
          >
            {isActive && <span className="pulse-ring absolute inset-0 -m-3 rounded-full border border-cyan-400/70" />}
            <span
              className="relative flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-500"
              style={{
                background: reached ? "linear-gradient(135deg,#00e5ff,#0088ff)" : "rgba(11,23,48,0.85)",
                borderColor: reached ? "#00e5ff" : "rgba(148,163,184,0.4)",
                boxShadow: reached ? "0 0 14px rgba(0,229,255,0.7)" : "none",
                transform: isActive ? "scale(1.18)" : "scale(1)",
              }}
            >
              <DynIcon name={stages[i].icon} className="h-4 w-4" style={{ color: reached ? "#041021" : "#64748b" }} />
            </span>
          </button>
        );
      })}
    </div>
  );
}
